import type { TimeseriesChartState } from './types/state.js';
import {
  TimeseriesChartConfigProperties,
  TimeseriesChartLegendConfig,
  TimeseriesChartThresholdsConfig,
  TimeseriesChartYAxisConfig,
} from './types/timeseries-config.js';
import { GapPolicy, PointsDisplay } from './types/timeseries-props.js';
import { TimeseriesChartToolbarConfig } from './types/toolbar.js';
import { initialSelectionState } from '../core/components/annotations/providers/AnnotationsDataInitialState.js';
import { defaultRatio, SCALE_FALLBACK_COLOR } from '../core/constants.js';
import { ChartInteractionsConfig } from '../core/types/interactions.js';
import { ValueRepresentation } from '../core/types/scales.js';
import { SeriesCurve } from '../core/types/timeseries.js';
import { ToolbarPlacement } from '../core/types/toolbar.js';
import { TooltipVariant } from '../core/types/tooltip.js';

export const defaultTooltipVariant: TooltipVariant = 'single';
export const defaultSeriesDisplayMode = 'multi-line';

export const defaultYAxis: TimeseriesChartYAxisConfig = {};

export const defaultGapPolicy: GapPolicy = 'gap';
export const defaultSeriesCurve: SeriesCurve = 'linear';
export const defaultPointsDisplay: PointsDisplay = 'auto';
export const defaultValueRepresentation: ValueRepresentation = 'absolute';
export const defaultInfiniteZoom = false;

export const DEFAULT_SCALE_FALLBACK_COLOR = SCALE_FALLBACK_COLOR;

export const defaultLegend: TimeseriesChartLegendConfig = {
  hidden: false,
  position: 'auto',
  ratio: defaultRatio,
};

export const defaultToolbar: TimeseriesChartToolbarConfig = {
  placement: 'top-right' as ToolbarPlacement,
  enabled: false,
};

export const defaultInteractions: ChartInteractionsConfig = {
  pan: {
    enabled: false,
  },
  zoom: {
    enabled: false,
  },
  zoomX: {
    enabled: false,
  },
};

export const defaultColorPalette = 'categorical';

export const defaultTimeseriesChartConfig: TimeseriesChartConfigProperties = {
  legend: defaultLegend,
  colorPalette: defaultColorPalette,
  gapPolicy: defaultGapPolicy,
  pointsDisplay: defaultPointsDisplay,
  valueRepresentation: defaultValueRepresentation,
  tooltip: {
    variant: defaultTooltipVariant,
    seriesDisplayMode: defaultSeriesDisplayMode,
  },
  toolbar: defaultToolbar,
};

export const defaultTimeseriesPerVariant = {
  line: [],
  area: [],
  bar: [],
  band: [],
  shapePriority: [],
};

export const defaultThreshold: TimeseriesChartThresholdsConfig = {
  left: [],
  right: [],
};

export const TIMESERIES_CHART_DEFAULT_VARIANT = 'line';

export const zIndicesTimeseriesAreas = {
  plot: 1,
  overlays: 2,
};

export const initialState: TimeseriesChartState = {
  filteredSeriesIds: new Set(),
  highlightedSeriesIds: undefined,
  annotations: initialSelectionState,
  zoom: {
    zoomInEnabled: true,
    zoomOutEnabled: false,
  },
  currentDomain: undefined,
  select: undefined,
  interactionMode: 'explore',
  explore: {
    visible: false,
    pinned: false,
    position: undefined,
  },
  thresholds: { hoveredThresholdId: undefined },
  toolbar: {
    visible: false,
  },
};

export const TIMESERIES_ARIA_LABEL = 'Timeseries Chart';
